/**
 * Centralized route permission engine — single source of truth.
 *
 * Rules format:
 *   { test, allow }
 *   test  — string (exact match) | (path: string) => boolean (pattern)
 *   allow — (user, access) => boolean
 *
 * Routes NOT listed here are accessible to any authenticated user.
 * PORTAL_ADMIN bypasses all rules (full access).
 *
 * Keep this file in sync whenever a new facility/role-gated route is added.
 */

const ROUTE_RULES = [

  // ─── EXTERNAL: fl_stock ────────────────────────────────────────────────────
  {
    test: '/FLStockOrder',
    allow: (u, a) => u?.userType === 'EXTERNAL' && a?.fl_stock === 'T',
  },
  {
    test: '/flStockReports',
    allow: (u, a) =>
      u?.userType === 'EXTERNAL' &&
      a?.fl_stock === 'T' &&
      (u?.userRole === 'FACILITY_USER' || u?.userRole === 'FACILITY_ADMIN'),
  },
  {
    test: '/approvalQue',
    allow: (u, a) =>
      u?.userType === 'EXTERNAL' &&
      a?.fl_stock === 'T' &&
      u?.userRole === 'FACILITY_ADMIN',
  },
  {
    // /stockOrder is shown only when FL stock is NOT enabled for the facility
    test: '/stockOrder',
    allow: (u, a) => u?.userType === 'EXTERNAL' && a?.fl_stock !== 'T',
  },
  {
    // Accessible to all EXTERNAL users — present in both fl_stock and non-fl_stock menus
    test: (p) => p === '/epcis-viewer' || p.startsWith('/epcis-viewer/'),
    allow: (u) => u?.userType === 'EXTERNAL',
  },

  // ─── EXTERNAL: access-flag gated ──────────────────────────────────────────
  {
    test: '/delivery',
    allow: (u, a) => u?.userType === 'EXTERNAL' && a?.delivery_recon === 'T',
  },
  {
    test: '/createOrder',
    allow: (u, a) => u?.userType === 'EXTERNAL' && a?.new_order === 'T',
  },
  {
    test: '/refill',
    allow: (u, a) => u?.userType === 'EXTERNAL' && a?.batch_refill === 'T',
  },
  {
    test: '/refillDue',
    allow: (u, a) => u?.userType === 'EXTERNAL' && a?.refill_due === 'T',
  },
  {
    test: '/nonforms',
    allow: (u, a) => u?.userType === 'EXTERNAL' && a?.non_form === 'T',
  },
  {
    test: '/clarifications',
    allow: (u, a) => u?.userType === 'EXTERNAL' && a?.clarifications === 'T',
  },

  // ─── EXTERNAL: EMAR gated ─────────────────────────────────────────────────
  {
    test: (p) =>
      ['/configure', '/createStatMeds', '/medpass', '/kop', '/kop-print-label', '/addendum-medpass'].includes(p),
    allow: (u, a) =>
      u?.userType === 'EXTERNAL' &&
      a?.emar === 'T' &&
      u?.userRole !== 'PROVIDER',
  },
  {
    // PROVIDER can view MAR; all other EMAR routes are blocked above
    test: (p) => p === '/medHistory' || p === '/medhistory',
    allow: (u, a) => u?.userType === 'EXTERNAL' && a?.emar === 'T',
  },

  // ─── EXTERNAL: always accessible (any role) ────────────────────────────────
  {
    test: (p) =>
      ['/returns', '/returnForm', '/patientDetails', '/patients',
        '/createPatient', '/invoiceList', '/publishList'].includes(p) ||
      p.startsWith('/patient/'),
    allow: (u) => u?.userType === 'EXTERNAL',
  },
  {
    // Stocks reports: EXTERNAL facility roles only
    test: '/stocksReports',
    allow: (u) =>
      (u?.userType === 'EXTERNAL' &&
        (u?.userRole === 'FACILITY_USER' || u?.userRole === 'FACILITY_ADMIN')) ||
      (u?.userType === 'INTERNAL' && u?.department === 'ORDER_ENTRY'),
  },
  {
    // Legacy facility-specific forms
    test: (p) => p === '/forms/kop' || p === '/forms/rmf',
    allow: (u) => u?.userType === 'EXTERNAL' && u?.facility === 1492,
  },

  // ─── INTERNAL: ORDER_ENTRY or FULFILLMENT ─────────────────────────────────
  {
    test: (p) => ['/shipingScan', '/sorterscan'].includes(p),
    allow: (u) =>
      u?.userType === 'INTERNAL' &&
      (u?.department === 'ORDER_ENTRY' || u?.department === 'FULFILLMENT'),
  },
  {
    test: (p) => ['/orderEntryDashboard', '/shipingReport'].includes(p),
    allow: (u) =>
      u?.userType === 'INTERNAL' &&
      (u?.department === 'ORDER_ENTRY' || u?.department === 'FULFILLMENT'),
  },

  // ─── INTERNAL: ORDER_ENTRY ─────────────────────────────────────────────────
  {
    test: (p) =>
      ['/stocks', '/copyDrugs', '/faciltyRefillDue',
        '/expirationReport', '/expirationPage'].includes(p),
    allow: (u) => u?.userType === 'INTERNAL' && u?.department === 'ORDER_ENTRY',
  },
  {
    test: (p) =>
      ['/managementFileUpload', '/managementReportDownld', '/managementReport'].includes(p),
    allow: (u) =>
      u?.userType === 'INTERNAL' &&
      u?.department === 'ORDER_ENTRY' &&
      u?.userRole === 'DEPARTMENT_ADMIN',
  },
  {
    test: '/refillTime',
    allow: (u) =>
      u?.userType === 'INTERNAL' &&
      u?.department === 'ORDER_ENTRY' &&
      u?.userRole === 'DEPARTMENT_ADMIN',
  },

  // ─── INTERNAL: cross-department ────────────────────────────────────────────
  {
    test: '/flStockOrderForm',
    allow: (u) =>
      u?.userType === 'INTERNAL' &&
      (u?.department === 'ORDER_ENTRY' || u?.department === 'INVENTORY'),
  },
  {
    test: '/claimSubmission',
    allow: (u) =>
      u?.userType === 'INTERNAL' &&
      (u?.department === 'ORDER_ENTRY' || u?.department === 'ACCOUNTING'),
  },
  {
    test: '/invalidOrderDrug',
    allow: (u) =>
      u?.userType === 'INTERNAL' &&
      (u?.department === 'ORDER_ENTRY' ||
        u?.department === 'ACCOUNTING' ||
        u?.department === 'CLINICAL'),
  },
  {
    test: '/replacements',
    allow: (u) =>
      u?.userType === 'INTERNAL' &&
      (u?.department === 'ORDER_ENTRY' || u?.department === 'INVENTORY'),
  },
  {
    test: '/flowrackinventory',
    allow: (u) =>
      u?.userType === 'INTERNAL' &&
      (u?.department === 'FULFILLMENT' || u?.department === 'INVENTORY'),
  },

  // ─── INTERNAL: ACCOUNTING ──────────────────────────────────────────────────
  {
    test: (p) =>
      ['/invoice', '/eom', '/eomFiles', '/eom-addons', '/eom-update'].includes(p),
    allow: (u) => u?.userType === 'INTERNAL' && u?.department === 'ACCOUNTING',
  },

  // ─── INTERNAL: CLINICAL ────────────────────────────────────────────────────
  {
    test: '/nonformsSuggestion',
    allow: (u) => u?.userType === 'INTERNAL' && u?.department === 'CLINICAL',
  },
];

// ─────────────────────────────────────────────────────────────────────────────

function matchesPath(test, path) {
  return typeof test === 'function' ? test(path) : test === path;
}

/**
 * Returns true if `path` is accessible for the given user + facility access combination.
 *
 * @param {string} path         - Current URL pathname (window.location.pathname)
 * @param {object} user         - Redux user object (userType, userRole, department, facility, …)
 * @param {object|null} access  - Facility access flags from GET /api/Remote/Access/{facilityId},
 *                                or null while still loading (prevents premature redirects)
 * @returns {boolean}
 */
export function canAccessRoute(path, user, access) {
  if (!user) return false;

  // PORTAL_ADMIN has unrestricted access to all routes
  if (user.userRole === 'PORTAL_ADMIN') return true;

  // Normalise: strip query string and trailing slash
  const normalizedPath = path.split('?')[0].replace(/\/$/, '') || '/';

  // EXTERNAL users: while facility access is still loading (null), do not redirect
  if (user.userType === 'EXTERNAL' && access === null) return true;

  const rule = ROUTE_RULES.find((r) => matchesPath(r.test, normalizedPath));
  if (rule) return rule.allow(user, access ?? {});

  // Route not explicitly gated → accessible to any authenticated user
  return true;
}
